/**
 * ARVEXIS PREMIUM REFLECTION EXPERIENCE
 * =====================================
 * 
 * "This doesn't feel like an AI app. It feels like opening 
 *  my own beautifully crafted dream journal."
 * 
 * Design Philosophy:
 * - Calm, premium, personal — not an AI report
 * - Dream artwork as emotional entry point
 * - Progressive disclosure with breathing room
 * - Elegant journal-style cards
 * - Conversation as natural continuation
 * 
 * Anticipation Sequence:
 * "Looking across your dream…" → "One image quietly stands out…" 
 * → "The reflection is beginning…" → Artwork fades in → Reflection
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Play, 
  Pause, 
  Download,
  Copy, 
  Check, 
  Share2,
  ChevronDown,
  MessageCircle,
  Sparkles,
  Loader2,
  BookOpen,
  Bookmark,
  Lock,
  HelpCircle,
  Leaf,
  Moon
} from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArvexisSymbol } from './ArvexisSymbol';
import DreamConversation from './DreamConversation';
import DreamCard from './DreamCard';
import { memberTimeOptions } from '../utils/memberTime';
// Dream/Reflection-owned playback floor. Isolated from Message's session by design: nothing here
// can stop Message Read Aloud, and Message never registers here.
import {
  claimFloor,
  currentFloor,
  isCurrentFloor,
  setFloorState,
  releaseFloor,
  stopCurrentFloor,
  subscribeFloor,
  reflectionAudio
} from '../utils/dreamReflectionPlayback';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The access token lives 15 minutes and a reflection is often read for far longer, so a
// Listen tap can land on an expired session. Refresh once and retry, the same way Chat does,
// instead of failing silently.
const postWithAuthRetry = async (url, body, config) => {
  try {
    return await axios.post(url, body, config);
  } catch (error) {
    if (error.response?.status === 401) {
      await axios.post(`${API}/auth/refresh`, {});
      return await axios.post(url, body, config);
    }
    throw error;
  }
};

// =============================================================================
// ARTWORK PATHS - Derived from emotion (same as App.js ReflectionCardV2)
// =============================================================================

const artworkPaths = {
  // Positive emotions
  calm: '/artwork/positive/calm/calm-001.png',
  hopeful: '/artwork/positive/hopeful/hopeful-001.png',
  grateful: '/artwork/positive/grateful/grateful-001.png',
  joyful: '/artwork/positive/joyful/joyful-001.png',
  inspired: '/artwork/positive/inspired/inspired-001.png',
  peaceful: '/artwork/positive/peaceful/peaceful-001.png',
  content: '/artwork/positive/content/content-001.png',
  proud: '/artwork/positive/proud/proud-001.png',
  curious: '/artwork/positive/curious/curious-001.png',
  confident: '/artwork/positive/confident/confident-001.png',
  connected: '/artwork/positive/connected/connected-001.png',
  excited: '/artwork/positive/excited/excited-001.png',
  // Neutral emotions
  thoughtful: '/artwork/neutral/thoughtful/thoughtful-001.png',
  reflective: '/artwork/neutral/reflective/reflective-001.png',
  focused: '/artwork/neutral/focused/focused-001.png',
  present: '/artwork/neutral/present/present-001.png',
  open: '/artwork/neutral/open/open-001.png',
  uncertain: '/artwork/neutral/uncertain/uncertain-001.png',
  // Difficult emotions
  nostalgic: '/artwork/difficult/nostalgic/nostalgic-001.png',
  lonely: '/artwork/difficult/lonely/lonely-001.png',
  anxious: '/artwork/difficult/anxious/anxious-001.png',
  fearful: '/artwork/difficult/fearful/fearful-001.png',
  sad: '/artwork/difficult/sad/sad-001.png',
  overwhelmed: '/artwork/difficult/overwhelmed/overwhelmed-001.png',
  frustrated: '/artwork/difficult/frustrated/frustrated-001.png',
  confused: '/artwork/difficult/confused/confused-001.png',
  ashamed: '/artwork/difficult/ashamed/ashamed-001.png',
  restless: '/artwork/difficult/restless/restless-001.png',
  contemplative: '/artwork/difficult/contemplative/contemplative-001.png',
  yearning: '/artwork/difficult/yearning/yearning-001.png',
  // Core emotion mapping
  unsettled: '/artwork/difficult/restless/restless-001.png'
};

/**
 * Get artwork URL for a dream - prioritizes explicit URL, falls back to emotion-based artwork
 */
const getArtworkUrl = (dream) => {
  // DREAMS V1: this Dream's own generated artwork, reused everywhere it appears. Never regenerated
  // on open, share, keepsake or export.
  if (dream.artwork_image_id) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/images/${dream.artwork_image_id}`;
  }
  if (dream.artwork_url) return dream.artwork_url;
  if (dream.generated_artwork_url) return dream.generated_artwork_url;
  
  // Derive from emotion (same logic as ReflectionCardV2 in App.js)
  const emotion = dream.emotions?.[0] || dream.emotion || 'reflective';
  const emotionKey = emotion?.toLowerCase();
  return artworkPaths[emotionKey] || artworkPaths.reflective;
};

// =============================================================================
// CINEMATIC ANTICIPATION SEQUENCE
// =============================================================================

const AnticipationSequence = ({ onComplete, lang = 'en', skipAnimation = false }) => {
  const [stage, setStage] = useState(0);
  
  // Cinematic sequence stages
  const stages = [
    {
      text: lang === 'es' ? 'Observando tu sueño…' : 'Looking across your dream…',
      duration: 2000
    },
    {
      text: lang === 'es' ? 'Una imagen aparece suavemente…' : 'One image quietly stands out…',
      duration: 1800
    },
    {
      text: lang === 'es' ? 'La reflexión está comenzando…' : 'The reflection is beginning…',
      duration: 1500
    }
  ];
  
  useEffect(() => {
    if (skipAnimation) {
      onComplete();
      return;
    }
    
    if (stage < stages.length) {
      const timer = setTimeout(() => {
        setStage(s => s + 1);
      }, stages[stage].duration);
      return () => clearTimeout(timer);
    } else {
      // Final fade out
      const timer = setTimeout(() => {
        onComplete();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [stage, stages.length, onComplete, skipAnimation]);
  
  if (skipAnimation) return null;
  
  return (
    <motion.div 
      className="fixed inset-0 z-50 flex flex-col items-center justify-center px-8"
      style={{ background: '#07080D' }}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {/* Breathing Symbol */}
      <motion.div
        animate={{ 
          scale: [1, 1.04, 1],
          opacity: [0.5, 0.85, 0.5]
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="mb-10"
      >
        <ArvexisSymbol size={64} glow />
      </motion.div>
      
      {/* Sequence Text */}
      <div className="h-12 flex items-center justify-center">
        <AnimatePresence mode="wait">
          {stage < stages.length && (
            <motion.p
              key={stage}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-[#F4EFE7]/60 text-base md:text-lg tracking-wide text-center"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic' }}
            >
              {stages[stage].text}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
      
      {/* Subtle progress indicator */}
      <div className="flex gap-2 mt-8">
        {stages.map((_, i) => (
          <motion.div
            key={i}
            className="w-1.5 h-1.5 rounded-full"
            initial={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
            animate={{ 
              backgroundColor: i <= stage ? 'rgba(232, 197, 143, 0.7)' : 'rgba(255, 255, 255, 0.15)'
            }}
            transition={{ duration: 0.4 }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// =============================================================================
// CINEMATIC ARTWORK HERO
// =============================================================================

const ArtworkHero = ({ artworkUrl, title, emotion, lang, onImageLoad, onImageError }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const handleLoad = () => {
    setImageLoaded(true);
    onImageLoad?.();
  };
  
  if (!artworkUrl) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="relative -mx-5 md:mx-0 mb-10 overflow-hidden"
    >
      {/* Artwork Container */}
      <div className="relative overflow-hidden rounded-none md:rounded-3xl">
        {/* Placeholder: the artwork files are large, so this holds the space with something
            alive instead of an empty black window while the image arrives. */}
        {!imageLoaded && (
          <div
            className="absolute inset-0 aspect-[3/2] md:aspect-[16/9] max-h-[46vh] md:max-h-[52vh]"
            style={{
              background: 'linear-gradient(180deg, #0B0C13 0%, #090A11 55%, #07080D 100%)'
            }}
          >
            <motion.div
              className="absolute inset-0"
              animate={{ opacity: [0.25, 0.5, 0.25] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                background: 'radial-gradient(ellipse at 50% 40%, rgba(232,197,143,0.10) 0%, transparent 65%)'
              }}
            />
          </div>
        )}
        <img
          src={artworkUrl}
          alt={title || 'Dream artwork'}
          className="w-full aspect-[3/2] md:aspect-[16/9] object-cover max-h-[46vh] md:max-h-[52vh]"
          onLoad={handleLoad}
          onError={() => onImageError?.()}
          style={{
            opacity: imageLoaded ? 1 : 0,
            filter: imageLoaded ? 'none' : 'blur(20px)',
            transition: 'opacity 1.2s ease-out, filter 0.8s ease-out'
          }}
          data-testid="dream-artwork"
        />
        
        {/* Cinematic gradient fade */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(to bottom, 
              transparent 0%, 
              transparent 40%,
              rgba(7, 8, 13, 0.3) 60%,
              rgba(7, 8, 13, 0.85) 85%,
              #07080D 100%
            )`
          }}
        />
        
        {/* Saved reassurance — dreams are kept automatically */}
        <div className="absolute top-4 right-9 md:top-6 md:right-6">
          <span 
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] tracking-wide whitespace-nowrap"
            style={{
              background: 'rgba(7, 8, 13, 0.55)',
              border: '1px solid rgba(232, 197, 143, 0.28)',
              color: '#E8C58F',
              backdropFilter: 'blur(8px)'
            }}
            data-testid="dream-saved-chip"
          >
            <Bookmark size={11} />
            {lang === 'es' ? 'En tu Diario de Sueños' : 'In your Dream Journal'}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// JOURNAL CARD - Elegant content container
// =============================================================================

const JournalCard = ({ 
  children, 
  variant = 'default', 
  delay = 0,
  className = ''
}) => {
  const variants = {
    default: {
      bg: 'rgba(255,255,255,0.025)',
      border: 'rgba(255,255,255,0.06)'
    },
    gold: {
      bg: 'rgba(232, 197, 143, 0.04)',
      border: 'rgba(232, 197, 143, 0.12)'
    },
    transparent: {
      bg: 'transparent',
      border: 'transparent'
    }
  };
  
  const style = variants[variant] || variants.default;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`p-5 md:p-6 rounded-2xl ${className}`}
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`
      }}
    >
      {children}
    </motion.div>
  );
};

// =============================================================================
// SECTION TITLE
// =============================================================================

const SectionTitle = ({ children, icon: Icon }) => (
  <div className="flex items-center gap-2 mb-4">
    {Icon && <Icon size={14} className="text-[#E8C58F]/70" />}
    <h3 className="text-xs uppercase tracking-[0.18em] text-[#F4EFE7]/40 font-medium">
      {children}
    </h3>
  </div>
);

// =============================================================================
// SYMBOL PILL
// =============================================================================

const SymbolPill = ({ name, meaning, index }) => {
  const [expanded, setExpanded] = useState(false);
  // Older dreams stored symbols as plain strings, so they arrive with no meaning. Those
  // must not offer an arrow that opens onto nothing.
  const hasMeaning = !!(meaning && meaning.trim());
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
    >
      <button
        onClick={() => hasMeaning && setExpanded(!expanded)}
        disabled={!hasMeaning}
        className="w-full text-left"
        data-testid={`symbol-pill-${index}`}
      >
        <div 
          className="px-4 py-3.5 rounded-xl transition-all duration-300"
          style={{
            background: expanded ? 'rgba(232, 197, 143, 0.06)' : 'rgba(255,255,255,0.025)',
            border: `1px solid ${expanded ? 'rgba(232, 197, 143, 0.18)' : 'rgba(255,255,255,0.05)'}`
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-[#E8C58F] text-sm">✦</span>
              <span className="text-[#F4EFE7]/85 text-sm font-medium">{name}</span>
            </div>
            {hasMeaning && (
              <motion.div
                animate={{ rotate: expanded ? 180 : 0 }}
                transition={{ duration: 0.25 }}
              >
                <ChevronDown size={14} className="text-[#F4EFE7]/25" />
              </motion.div>
            )}
          </div>
          
          <AnimatePresence>
            {expanded && hasMeaning && (
              <motion.p
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.25 }}
                className="text-[#F4EFE7]/55 text-sm leading-relaxed pl-7"
              >
                {meaning}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </button>
    </motion.div>
  );
};

// =============================================================================
// SECTION LISTEN PILL — per-section playback control, always shows its own state
// =============================================================================

const SectionListenPill = ({ state, onClick, estimate, lang, testId }) => {
  const active = state === 'playing' || state === 'paused' || state === 'loading';
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full transition-all shrink-0"
      style={{
        background: active ? 'rgba(232,197,143,0.14)' : 'rgba(232,197,143,0.05)',
        border: `1px solid rgba(232,197,143,${active ? '0.4' : '0.16'})`
      }}
      data-testid={testId}
    >
      {state === 'loading' ? (
        <Loader2 size={13} className="text-[#E8C58F] animate-spin" />
      ) : state === 'playing' ? (
        <Pause size={13} className="text-[#E8C58F]" />
      ) : (
        <Play size={13} className="text-[#E8C58F]" />
      )}
      <span className="text-[#E8C58F] text-xs font-medium">
        {state === 'playing'
          ? (lang === 'es' ? 'Pausar' : 'Pause')
          : state === 'paused'
            ? (lang === 'es' ? 'Seguir' : 'Resume')
            : (lang === 'es' ? 'Escuchar' : 'Listen')}
      </span>
      {estimate && (
        <span className="text-[#E8C58F]/45 text-[11px] tabular-nums">{estimate}</span>
      )}
    </button>
  );
};

// =============================================================================
// NARRATED SECTION CARD — one part of Arvexis's spoken reflection
// =============================================================================

const NarratedSection = ({ 
  icon: Icon, 
  title, 
  variant = 'default', 
  delay = 0, 
  isActive = false, 
  listen, 
  extra, 
  bodyClass = 'text-[#F4EFE7]/78',
  children,
  testId
}) => (
  <JournalCard 
    delay={delay} 
    variant={variant} 
    className={`mb-5 transition-all duration-500 ${isActive ? 'ring-1 ring-[#E8C58F]/45' : ''}`}
  >
    <div className="flex items-start justify-between gap-3 mb-3.5" data-testid={testId}>
      <div className="flex items-center gap-2.5 min-w-0">
        {Icon && (
          <span 
            className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
            style={{ background: 'rgba(232,197,143,0.10)', border: '1px solid rgba(232,197,143,0.18)' }}
          >
            <Icon size={13} className="text-[#E8C58F]" />
          </span>
        )}
        <h2 
          className="text-[#F4EFE7]/90 text-base md:text-lg font-light leading-snug"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          {title}
        </h2>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {extra}
        {listen}
      </div>
    </div>
    <div 
      className={`text-[16px] md:text-[17px] leading-[1.72] ${bodyClass}`}
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}
    >
      {children}
    </div>
    {isActive && (
      <div className="mt-3.5 flex items-center gap-2">
        <motion.span 
          className="w-1.5 h-1.5 rounded-full bg-[#E8C58F]"
          animate={{ opacity: [0.35, 1, 0.35] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        />
        <span className="text-[11px] tracking-[0.12em] uppercase text-[#E8C58F]/70">
          Arvexis
        </span>
      </div>
    )}
  </JournalCard>
);

// =============================================================================
// REFLECTION QUESTION CARD
// =============================================================================

const ReflectionQuestion = ({ question, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    className="my-10 py-8 text-center relative"
  >
    {/* Decorative lines */}
    <div className="absolute left-1/2 -translate-x-1/2 top-0 w-16 h-px bg-gradient-to-r from-transparent via-[#E8C58F]/25 to-transparent" />
    <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-16 h-px bg-gradient-to-r from-transparent via-[#E8C58F]/25 to-transparent" />
    
    <p 
      className="text-[#F4EFE7]/70 text-lg md:text-xl leading-relaxed px-4 max-w-lg mx-auto"
      style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic' }}
    >
      &ldquo;{question}&rdquo;
    </p>
  </motion.div>
);

// =============================================================================
// SECTION DIVIDER
// =============================================================================

const SectionDivider = ({ variant = 'subtle' }) => (
  <div className="my-10 flex items-center justify-center">
    {variant === 'dots' ? (
      <div className="flex gap-2">
        <div className="w-1 h-1 rounded-full bg-white/10" />
        <div className="w-1 h-1 rounded-full bg-[#E8C58F]/40" />
        <div className="w-1 h-1 rounded-full bg-white/10" />
      </div>
    ) : (
      <div className="w-20 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
    )}
  </div>
);

// =============================================================================
// EXPLORE TOGETHER CTA
// =============================================================================

const ExploreCTA = ({ onExplore, lang = 'en', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    className="my-10"
  >
    <div 
      className="p-7 md:p-9 rounded-2xl text-center relative overflow-hidden"
      style={{
        background: 'linear-gradient(145deg, rgba(232,197,143,0.06) 0%, rgba(232,197,143,0.015) 100%)',
        border: '1px solid rgba(232,197,143,0.12)'
      }}
    >
      {/* Subtle ambient glow */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(232,197,143,0.04) 0%, transparent 70%)'
        }}
      />
      
      <div className="relative z-10">
        <motion.div
          animate={{ 
            scale: [1, 1.03, 1],
            opacity: [0.75, 1, 0.75]
          }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="mb-5"
        >
          <ArvexisSymbol size={44} />
        </motion.div>
        
        <p 
          className="text-[#F4EFE7]/85 text-lg mb-2"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic' }}
        >
          {lang === 'es' 
            ? '¿Te gustaría explorar esto juntos?' 
            : 'Would you like to explore this together?'}
        </p>
        <p className="text-[#F4EFE7]/45 text-sm mb-6 max-w-xs mx-auto">
          {lang === 'es'
            ? 'Una conversación para explorar lo que más te llamó la atención.'
            : 'A conversation to explore what stood out to you.'}
        </p>
        
        <button
          onClick={onExplore}
          className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-sm font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{ 
            background: 'rgba(232, 197, 143, 0.06)',
            border: '1px solid rgba(232, 197, 143, 0.56)',
            color: '#F4EFE7'
          }}
          data-testid="explore-dream-btn"
        >
          <MessageCircle size={17} />
          {lang === 'es' ? 'Explorar Juntos' : 'Explore Together'}
        </button>
      </div>
    </div>
  </motion.div>
);

// =============================================================================
// MAIN PREMIUM REFLECTION SCREEN
// =============================================================================

const PremiumReflectionScreen = ({ lang = 'en', t = {} }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  
  // Core State
  const [dream, setDream] = useState(location.state?.dream || null);
  const [loading, setLoading] = useState(!location.state?.dream);
  
  // Opening a Dream that already has its Reflection opens the Reflection — nothing else. The
  // anticipation sequence belongs only to the moment a Reflection is genuinely being created,
  // which is the one path that arrives here with `fromNewDream`.
  const fromNewDream = location.state?.fromNewDream === true;

  const getInitialAnticipationState = () => {
    if (!fromNewDream) return false;
    const dreamId = location.state?.dream?.id || location.state?.dream?._id;
    if (!dreamId) return true;
    return !sessionStorage.getItem(`reflection_seen_${dreamId}`);
  };
  
  const [showAnticipation, setShowAnticipation] = useState(getInitialAnticipationState);
  const [artworkLoaded, setArtworkLoaded] = useState(false);
  const [artworkFailed, setArtworkFailed] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [dreamExpanded, setDreamExpanded] = useState(false);
  const [transcriptOverflows, setTranscriptOverflows] = useState(false);
  const transcriptRef = useRef(null);
  
  // Save and Share both lead to the Dream Card, which owns the image, its progress and its
  // own truthful confirmations.
  const [showDreamCard, setShowDreamCard] = useState(false);
  
  // TTS PLAYBACK STATE — who is speaking lives in the Dream/Reflection floor at module scope, not
  // in this mount, so walking to another part of Arvexis no longer ends the reflection. This mount
  // mirrors that truth; it does not own it.
  const [floorSnapshot, setFloorSnapshot] = useState(currentFloor);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [playbackDuration, setPlaybackDuration] = useState(0);
  const speakingHere = floorSnapshot.owner === 'reflection';
  const playbackState = speakingHere ? floorSnapshot.state : 'idle';
  // Which section she is speaking right now, so the page can show it instead of leaving
  // the listener guessing.
  const activeSectionKey = speakingHere ? floorSnapshot.itemKey : null;
  const clipCacheRef = useRef({});
  const sectionIndexRef = useRef(0);
  const advanceTimerRef = useRef(null);
  const modeRef = useRef('master');
  const [showAllSymbols, setShowAllSymbols] = useState(false);
  
  // Refs
  const contentRef = useRef(null);
  
  // ==========================================================================
  // DATA FETCHING
  // ==========================================================================
  
  useEffect(() => {
    if (location.state?.dream) {
      setDream(location.state.dream);
      setLoading(false);
      return;
    }
    
    let cancelled = false;

    const fetchDream = async () => {
      try {
        const response = await axios.get(`${API}/dreams/${id}`);
        let data = response.data;

        // EN/ES parity: Arvexis's Reflection is read in the member's language. The member's own
        // Dream is never rewritten — the original transcript and its language are preserved and
        // labelled, so translated Reflection and original Dream stay truthfully distinct.
        if (lang && data.language && lang !== data.language) {
          try {
            const localized = await axios.get(`${API}/dreams/${id}/localized?lang=${lang}`);
            if (localized.data) {
              data = {
                ...localized.data,
                transcript: data.transcript,
                original_language: data.language,
                is_translated: true,
              };
            }
          } catch (e) {
            // A failed translation must never cost the member their Reflection.
            console.warn('Localized reflection unavailable, showing original language');
          }
        }

        if (cancelled) return;
        setDream(data);

        // This Dream's artwork is made concurrently with the Reflection, so a brand new Dream can
        // arrive a moment before its artwork. Look again a couple of times, then stop and let the
        // emotion plate stand in. Never blocks reading.
        if (!data.artwork_image_id) {
          [7000, 15000].forEach((delay) => {
            setTimeout(async () => {
              if (cancelled) return;
              try {
                const again = await axios.get(`${API}/dreams/${id}`);
                if (!cancelled && again.data?.artwork_image_id) {
                  setDream((prev) =>
                    prev && !prev.artwork_image_id
                      ? { ...prev, artwork_image_id: again.data.artwork_image_id }
                      : prev
                  );
                }
              } catch (e) { /* silent: artwork is optional */ }
            }, delay);
          });
        }
      } catch (err) {
        console.error("Failed to fetch dream:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    
    fetchDream();
    return () => { cancelled = true; };
  }, [id, location.state?.dream, lang]);
  
  // ==========================================================================
  // ANTICIPATION SEQUENCE LOGIC
  // ==========================================================================
  
  useEffect(() => {
    if (dream) {
      // An already-reflected Dream opened from the Dream Journal never runs the sequence.
      if (!fromNewDream) {
        setShowAnticipation(false);
        return;
      }
      // Use the artwork helper that derives from emotion if no explicit URL
      const artworkUrl = getArtworkUrl(dream);
      const hasArtwork = !!artworkUrl;
      const seenBefore = sessionStorage.getItem(`reflection_seen_${dream.id || dream._id}`);
      
      // Skip animation if no artwork or if seen before this session
      if (!hasArtwork || seenBefore) {
        setShowAnticipation(false);
      } else {
        // Mark as seen for this session (shorten/skip on repeat visits)
        sessionStorage.setItem(`reflection_seen_${dream.id || dream._id}`, 'true');
      }
    }
  }, [dream, fromNewDream]);
  
  const handleAnticipationComplete = useCallback(() => {
    setShowAnticipation(false);
  }, []);
  
  // Only offer "read the full dream" when there is actually more to read.
  useEffect(() => {
    if (dreamExpanded) return;
    const el = transcriptRef.current;
    if (!el) return;
    setTranscriptOverflows(el.scrollHeight > el.clientHeight + 2);
  }, [dream, dreamExpanded, showAnticipation]);
  
  // ==========================================================================
  // TTS PLAYBACK — read in the order the page is written
  //
  // Listen used to speak three sections as one clip while the only highlight sat on the
  // interpretation card, so it sounded like she was jumping around the page. Now each
  // section is spoken as its own clip, in display order, and the card being spoken is the
  // card that lights up. Nova, the wording and the section order are unchanged; only
  // delivery and the visual mapping changed. Key Symbols and the Reflection Question stay
  // visible and explorable and are deliberately not narrated.
  // ==========================================================================
  
  const playbackSections = useMemo(() => {
    if (!dream) return [];
    const es = lang === 'es';
    const sections = [];
    if (dream.emotional_summary) {
      sections.push({
        key: 'emotional_summary',
        label: es ? 'Resumen Emocional' : 'Emotional Summary',
        text: dream.emotional_summary,
        cue: es ? 'Empiezo por cómo se siente este sueño.' : "Let me start with how this dream feels."
      });
    }
    if (dream.integrated_interpretation) {
      sections.push({
        key: 'interpretation',
        label: es ? 'La Reflexión de Arvexis' : "Arvexis's Reflection",
        text: dream.integrated_interpretation,
        cue: es ? 'Y esto es lo que percibo en él.' : "Here's what I'm noticing in it."
      });
    }
    if (dream.grounding_insight) {
      sections.push({
        key: 'grounding',
        label: es ? 'Perspectiva de Conexión' : 'Grounding Insight',
        text: dream.grounding_insight,
        cue: es ? 'Y algo para llevarte contigo.' : 'And one thing to carry with you.'
      });
    }
    return sections;
  }, [dream, lang]);
  
  // Rough spoken length so each Listen control can say how long it is before it starts.
  const estimateClipTime = (text) => {
    const words = (text || '').trim().split(/\s+/).filter(Boolean).length;
    const seconds = Math.max(5, Math.round((words / 145) * 60 / 0.95));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  };
  
  const fetchSectionClip = async (section, withCue, signal) => {
    const cacheKey = withCue ? `${section.key}:cue` : section.key;
    if (clipCacheRef.current[cacheKey]) return clipCacheRef.current[cacheKey];
    const spoken = withCue && section.cue ? `${section.cue} ${section.text}` : section.text;
    const response = await postWithAuthRetry(`${API}/tts`, {
      text: spoken,
      speed: 0.95,
      language: dream?.language === 'es' ? 'es' : 'en'
    }, { timeout: 30000, signal });
    if (!response.data?.success || !response.data?.audio_base64) {
      throw new Error('Invalid TTS response');
    }
    const src = `data:audio/mp3;base64,${response.data.audio_base64}`;
    clipCacheRef.current[cacheKey] = src;
    return src;
  };
  
  const clearAdvance = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };
  
  const resetPlayback = () => {
    clearAdvance();
    stopCurrentFloor();
    setPlaybackProgress(0);
    setPlaybackDuration(0);
    sectionIndexRef.current = 0;
    modeRef.current = 'master';
  };
  
  // Each tap is a request that carries a generation. If it is replaced while its audio is still
  // being synthesized, the late response is discarded instead of seizing a floor it no longer holds.
  const playSection = async (index, mode = 'master') => {
    const section = playbackSections[index];
    if (!section) {
      resetPlayback();
      return;
    }
    clearAdvance();
    modeRef.current = mode;
    sectionIndexRef.current = index;
    // Created inside the tap: iOS only allows programmatic playback on an element a gesture unlocked.
    const audio = reflectionAudio();
    const controller = new AbortController();
    const generation = claimFloor({
      owner: 'reflection',
      itemKey: section.key,
      abort: controller,
      stop: () => { try { audio.pause(); } catch (e) { /* already stopped */ } }
    });
    setPlaybackProgress(0);
    try {
      const withCue = mode === 'master';
      const src = await fetchSectionClip(section, withCue, controller.signal);
      if (!isCurrentFloor(generation)) return;
      audio.src = src;
      audio.onloadedmetadata = () => setPlaybackDuration(audio.duration);
      audio.ontimeupdate = () => setPlaybackProgress(audio.currentTime);
      audio.onended = () => {
        if (!isCurrentFloor(generation)) return;
        if (mode === 'master' && index + 1 < playbackSections.length) {
          // A short breath between parts so the sections do not run together.
          advanceTimerRef.current = setTimeout(() => playSection(index + 1, 'master'), 700);
        } else {
          releaseFloor(generation);
        }
      };
      audio.onerror = () => setFloorState(generation, 'error');
      await audio.play();
      if (!isCurrentFloor(generation)) {
        try { audio.pause(); } catch (e) { /* already stopped */ }
        return;
      }
      setFloorState(generation, 'playing');
      // Warm the next clip while this one plays so the seam between parts stays short.
      const next = playbackSections[index + 1];
      if (mode === 'master' && next) fetchSectionClip(next, true).catch(() => {});
    } catch (error) {
      // A replaced request that was cancelled mid-flight is not a failure the member should see.
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || error?.name === 'AbortError') return;
      if (!isCurrentFloor(generation)) return;
      console.error('Dream TTS error:', error);
      setFloorState(generation, 'error');
    }
  };
  
  const handlePlayReflection = () => {
    if (!playbackSections.length) return;
    
    if (speakingHere && playbackState === 'playing') {
      reflectionAudio().pause();
      setFloorState(floorSnapshot.generation, 'paused');
      return;
    }
    if (speakingHere && playbackState === 'paused' && modeRef.current === 'master') {
      reflectionAudio().play().catch(() => {});
      setFloorState(floorSnapshot.generation, 'playing');
      return;
    }
    if (speakingHere && (playbackState === 'paused' || modeRef.current === 'solo')) {
      // Coming from a single-section listen: start the walkthrough from the top.
      playSection(0, 'master');
      return;
    }
    playSection(playbackState === 'error' ? sectionIndexRef.current : 0, 'master');
  };
  
  // One section on its own — no transition cue, no advance to the next part.
  const handleSectionListen = (key) => {
    const index = playbackSections.findIndex((s) => s.key === key);
    if (index < 0) return;
    
    if (speakingHere && activeSectionKey === key && playbackState === 'playing') {
      reflectionAudio().pause();
      setFloorState(floorSnapshot.generation, 'paused');
      return;
    }
    if (speakingHere && activeSectionKey === key && playbackState === 'paused' && modeRef.current === 'solo') {
      reflectionAudio().play().catch(() => {});
      setFloorState(floorSnapshot.generation, 'playing');
      return;
    }
    playSection(index, 'solo');
  };
  
  const sectionPlaybackState = (key) => (activeSectionKey === key ? playbackState : 'idle');
  
  const handleStopPlayback = () => {
    clearAdvance();
    stopCurrentFloor();
    setPlaybackProgress(0);
    setPlaybackDuration(0);
    sectionIndexRef.current = 0;
    modeRef.current = 'master';
  };
  
  const handleSeek = (e) => {
    if (!speakingHere) return;
    const audio = reflectionAudio();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audio.currentTime = percentage * playbackDuration;
  };
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // SUBSCRIBE, DO NOT OWN. Unmounting unsubscribes this mount's UI; it no longer silences her.
  // A returning mount reads the floor's truth — which section, which state — instead of trusting
  // its own state from before it left.
  useEffect(() => {
    const unsubscribe = subscribeFloor(setFloorSnapshot);
    setFloorSnapshot(currentFloor());
    return () => {
      // The advance timer belongs to this mount's walkthrough; the audio does not.
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      unsubscribe();
    };
  }, []);
  
  // Re-attach progress reporting when the member comes back while she is still speaking. The
  // `onended` handler set by the still-running request is deliberately left alone.
  useEffect(() => {
    const floor = currentFloor();
    if (floor.owner !== 'reflection') return;
    const audio = reflectionAudio();
    audio.onloadedmetadata = () => setPlaybackDuration(audio.duration);
    audio.ontimeupdate = () => setPlaybackProgress(audio.currentTime);
    setPlaybackProgress(audio.currentTime || 0);
    setPlaybackDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
  }, []);
  
  // ==========================================================================
  // COPY HANDLERS
  // ==========================================================================
  
  const handleCopy = async (text, feedbackKey) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(feedbackKey);
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (err) {
      // Fallback
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopyFeedback(feedbackKey);
        setTimeout(() => setCopyFeedback(null), 2000);
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
      }
    }
  };
  
  // ==========================================================================
  // SAVE / SHARE
  // ==========================================================================
  
  // Save and Share both open the Dream Card. The legacy Keepsake surface, its PDF export and
  // its share sheet are no longer reachable from the Reflection.
  const handleOpenDreamCard = () => {
    setShowDreamCard(true);
  };
  
  // ==========================================================================
  // HELPERS
  // ==========================================================================
  
  const getEmotionLabel = (emotion) => {
    const labels = {
      en: {
        peaceful: 'Peaceful', anxious: 'Anxious', joyful: 'Joyful',
        confused: 'Confused', nostalgic: 'Nostalgic', hopeful: 'Hopeful',
        fearful: 'Fearful', curious: 'Curious', sad: 'Sad',
        excited: 'Excited', serene: 'Serene', mysterious: 'Mysterious'
      },
      es: {
        peaceful: 'Pacífico', anxious: 'Ansioso', joyful: 'Alegre',
        confused: 'Confundido', nostalgic: 'Nostálgico', hopeful: 'Esperanzado',
        fearful: 'Temeroso', curious: 'Curioso', sad: 'Triste',
        excited: 'Emocionado', serene: 'Sereno', mysterious: 'Misterioso'
      }
    };
    return t?.emotions?.[emotion] || labels[lang]?.[emotion] || emotion;
  };
  
  // ==========================================================================
  // RENDER: LOADING STATE
  // ==========================================================================
  
  if (loading) {
    return (
      <div
        className="min-h-screen bg-[#07080D] flex flex-col items-center justify-center"
        data-testid="reflection-loading"
      >
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ArvexisSymbol size={48} />
        </motion.div>
        {/* A genuine technical wait says only that. Nothing here implies a new Reflection. */}
        <p
          className="text-[#F4EFE7]/45 text-sm mt-6"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic' }}
        >
          {lang === 'es' ? 'Cargando tu reflexión…' : 'Loading your reflection…'}
        </p>
      </div>
    );
  }
  
  // ==========================================================================
  // RENDER: NOT FOUND STATE
  // ==========================================================================
  
  if (!dream) {
    return (
      <div className="min-h-screen bg-[#07080D] flex flex-col items-center justify-center px-6">
        <div className="w-16 h-16 mb-4 rounded-2xl bg-white/[0.03] flex items-center justify-center border border-white/[0.06]">
          <BookOpen className="w-8 h-8 text-[#F4EFE7]/40" />
        </div>
        <p className="text-[#F4EFE7]/60 mb-4">
          {lang === 'es' ? 'Reflexión no encontrada' : 'Reflection not found'}
        </p>
        <button 
          onClick={() => navigate('/journal')} 
          className="px-6 py-2.5 bg-[#E8C58F]/10 border border-[#E8C58F]/30 text-[#E8C58F] rounded-full text-sm font-medium"
        >
          {lang === 'es' ? 'Ir al diario' : 'Go to Journal'}
        </button>
      </div>
    );
  }
  
  // Use the artwork helper that derives from emotion if no explicit URL
  const artworkUrl = getArtworkUrl(dream);
  // A failed image must not leave a large empty hero: fall back to the plain title block.
  const hasArtwork = !!artworkUrl && !artworkFailed;
  const activeSectionIndex = playbackSections.findIndex((s) => s.key === activeSectionKey);
  const activeSection = activeSectionIndex >= 0 ? playbackSections[activeSectionIndex] : null;
  // Only symbols that actually carry a meaning are worth showing.
  const symbolsWithMeaning = (dream.symbols || []).filter((s) => s?.name && (s.meaning || '').trim());
  const visibleSymbols = showAllSymbols ? symbolsWithMeaning : symbolsWithMeaning.slice(0, 3);
  const dreamedOn = new Date(dream.created_at);
  const dreamedLabel = `${dreamedOn.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${dreamedOn.toLocaleTimeString(lang === 'es' ? 'es-ES' : 'en-US', memberTimeOptions({ hour: 'numeric', minute: '2-digit' }))}`;
  const totalListenTime = estimateClipTime(playbackSections.map((s) => s.text).join(' '));
  
  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================
  
  return (
    <>
      {/* Cinematic Anticipation Sequence */}
      <AnimatePresence>
        {showAnticipation && hasArtwork && (
          <AnticipationSequence 
            onComplete={handleAnticipationComplete} 
            lang={lang}
          />
        )}
      </AnimatePresence>
      
      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showAnticipation ? 0 : 1 }}
        transition={{ duration: 1 }}
        className="min-h-screen pb-28 md:pb-12 overflow-x-hidden utility-surface"
        ref={contentRef}
        data-testid="premium-reflection-screen"
      >
        {/* Header */}
        <header 
          className="sticky top-0 z-20 flex items-center justify-between px-5 md:px-8 py-4 relative"
          style={{ 
            background: 'rgba(7, 8, 13, 0.85)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.05)'
          }}
        >
          <button 
            onClick={() => navigate('/journal')}
            className="flex items-center gap-2 text-[#F4EFE7]/55 hover:text-[#F4EFE7]/85 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline text-sm">
              {lang === 'es' ? 'Diario de Sueños' : 'Dream Journal'}
            </span>
          </button>
          
          <div className="absolute left-1/2 -translate-x-1/2 text-center pointer-events-none">
            <div 
              className="text-[#E8C58F] text-[13px] md:text-sm tracking-[0.35em]"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
            >
              ARVEXIS
            </div>
          </div>
          
          {/* The header carries no export or share action. Save and Share live beneath
              Continue Talking, and both lead to one place. */}
          <div className="flex items-center gap-3">
            <span className="text-[#F4EFE7]/35 text-sm">
              {new Date(dream.created_at).toLocaleDateString(
                lang === 'es' ? 'es-ES' : 'en-US', 
                { month: 'short', day: 'numeric' }
              )}
            </span>
          </div>
        </header>
        
        {/* Artwork Hero */}
        {hasArtwork && (
          <ArtworkHero 
            artworkUrl={artworkUrl}
            title={dream.title}
            emotion={getEmotionLabel(dream.emotion)}
            lang={lang}
            onImageLoad={() => setArtworkLoaded(true)}
            onImageError={() => setArtworkFailed(true)}
          />
        )}
        
        {/* Content Container */}
        <main className="px-5 md:px-8 max-w-2xl lg:max-w-[760px] mx-auto">
          
          {/* ─────────────────────────────────────────────────────────────
              DREAM DETAILS — emotion, when it was dreamed, title, and the
              original dream as a preview that expands.
             ───────────────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className={hasArtwork ? 'mb-7' : 'mb-7 pt-6'}
            data-testid="dream-details"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3">
              {dream.emotion && (
                <span 
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide"
                  style={{
                    background: 'rgba(232, 197, 143, 0.10)',
                    border: '1px solid rgba(232, 197, 143, 0.22)',
                    color: '#E8C58F'
                  }}
                  data-testid="dream-emotion-chip"
                >
                  <Moon size={11} />
                  {getEmotionLabel(dream.emotion)}
                </span>
              )}
              <span className="text-[#F4EFE7]/35 text-xs">
                {lang === 'es' ? 'Soñado el' : 'Dreamed on'} {dreamedLabel}
              </span>
            </div>
            
            <button
              onClick={() => transcriptOverflows && setDreamExpanded(!dreamExpanded)}
              disabled={!transcriptOverflows}
              className="w-full text-left group"
              data-testid="dream-details-toggle"
            >
              <div className="flex items-start justify-between gap-4 mb-2.5">
                <h1 
                  className="text-2xl md:text-3xl text-[#F4EFE7] font-light leading-tight"
                  style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
                >
                  {dream.title || (lang === 'es' ? 'Tu Sueño' : 'Your Dream')}
                </h1>
                {transcriptOverflows && (
                  <motion.div
                    animate={{ rotate: dreamExpanded ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="mt-1.5 shrink-0"
                  >
                    <ChevronDown size={18} className="text-[#F4EFE7]/30 group-hover:text-[#F4EFE7]/55 transition-colors" />
                  </motion.div>
                )}
              </div>
              
              <p 
                ref={transcriptRef}
                className="text-[#F4EFE7]/65"
                style={{ 
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                  fontSize: '15.5px',
                  lineHeight: 1.7,
                  overflow: 'hidden',
                  maxHeight: dreamExpanded ? '4000px' : '5.1em',
                  transition: 'max-height 0.35s ease'
                }}
                data-testid="dream-transcript"
              >
                {dreamExpanded ? dream.transcript : `“${dream.transcript}”`}
              </p>
              
              {/* When the Reflection is read in another language, the member's own Dream is still
                  shown in the words they recorded — and said so plainly. */}
              {dream.is_translated && (
                <span className="block mt-2.5 text-[11px] text-[#F4EFE7]/35" data-testid="original-dream-note">
                  {lang === 'es'
                    ? `Tu Sueño, en tus propias palabras (${dream.original_language === 'en' ? 'inglés' : dream.original_language}). La Reflexión está traducida al español.`
                    : `Your Dream, in the words you recorded (${dream.original_language === 'es' ? 'Spanish' : dream.original_language}). The Reflection is translated into English.`}
                </span>
              )}

              {transcriptOverflows && (
                <span className="inline-block mt-2.5 text-[11px] tracking-wide text-[#E8C58F]/55">
                  {dreamExpanded
                    ? (lang === 'es' ? 'Mostrar menos' : 'Show less')
                    : (lang === 'es' ? 'Leer el sueño completo' : 'Read the full dream')}
                </span>
              )}
            </button>
          </motion.div>
          
          {/* ── KEY SYMBOLS — visible and explorable, never narrated ── */}
          {symbolsWithMeaning.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mb-6 p-4 md:p-5 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.022)', border: '1px solid rgba(244,239,231,0.09)' }}
              data-testid="key-symbols-card"
            >
              <div className="flex items-center justify-between gap-3 mb-3.5">
                <div className="flex items-center gap-2.5">
                  <span 
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(232,197,143,0.10)', border: '1px solid rgba(232,197,143,0.18)' }}
                  >
                    <span className="text-[#E8C58F] text-xs">✦</span>
                  </span>
                  <h2 
                    className="text-[#F4EFE7]/90 text-base md:text-lg font-light"
                    style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
                  >
                    {lang === 'es' ? 'Símbolos Clave' : 'Key Symbols'}
                  </h2>
                </div>
                {symbolsWithMeaning.length > 3 && (
                  <button
                    onClick={() => setShowAllSymbols(!showAllSymbols)}
                    className="text-xs text-[#E8C58F]/70 hover:text-[#E8C58F] transition-colors shrink-0"
                    data-testid="see-all-symbols-btn"
                  >
                    {showAllSymbols
                      ? (lang === 'es' ? 'Ver menos' : 'See less')
                      : `${lang === 'es' ? 'Ver todos' : 'See All'} (${symbolsWithMeaning.length})`}
                  </button>
                )}
              </div>
              <div className="space-y-2.5">
                {visibleSymbols.map((sym, i) => (
                  <SymbolPill 
                    key={`${sym.name}-${i}`} 
                    name={sym.name} 
                    meaning={sym.meaning}
                    index={i}
                  />
                ))}
              </div>
            </motion.div>
          )}
          
          {/* ─────────────────────────────────────────────────────────────
              ARVEXIS'S REFLECTION — the narrated walkthrough. The master
              control sits at the top of the part it reads, and the card being
              spoken is the card that lights up.
             ───────────────────────────────────────────────────────────── */}
          {playbackSections.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mb-5 p-4 rounded-2xl"
              style={{
                background: activeSection
                  ? 'linear-gradient(145deg, rgba(232,197,143,0.10) 0%, rgba(232,197,143,0.025) 100%)'
                  : 'rgba(232,197,143,0.045)',
                border: `1px solid rgba(232,197,143,${activeSection ? '0.28' : '0.14'})`
              }}
            >
              <button
                onClick={handlePlayReflection}
                disabled={playbackState === 'loading'}
                className="w-full flex items-center justify-center gap-3 py-1"
                style={{ cursor: playbackState === 'loading' ? 'wait' : 'pointer' }}
                data-testid="listen-btn"
              >
                {playbackState === 'loading' ? (
                  <Loader2 size={18} className="text-[#E8C58F] animate-spin" />
                ) : playbackState === 'playing' ? (
                  <Pause size={18} className="text-[#E8C58F]" />
                ) : (
                  <Play size={18} className="text-[#E8C58F]" />
                )}
                <span className="text-[#E8C58F] font-medium text-[15px]">
                  {playbackState === 'loading' 
                    ? (lang === 'es' ? 'Preparando...' : 'Preparing...')
                    : playbackState === 'playing'
                      ? (lang === 'es' ? 'Pausar' : 'Pause')
                      : playbackState === 'paused'
                        ? (lang === 'es' ? 'Continuar escuchando' : 'Resume listening')
                        : playbackState === 'error'
                          ? (lang === 'es' ? 'Intentar de nuevo' : 'Try again')
                          : (lang === 'es' ? 'Escuchar a Arvexis' : 'Listen to Arvexis')
                  }
                </span>
              </button>
              
              {/* Where she is right now */}
              {activeSection && playbackState !== 'error' ? (
                <div className="mt-3" data-testid="playback-now-reading">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-[#E8C58F]/75 truncate">
                      {lang === 'es' ? 'Leyendo' : 'Reading'}: {activeSection.label}
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[11px] text-[#F4EFE7]/30 tabular-nums">
                        {activeSectionIndex + 1} / {playbackSections.length}
                      </span>
                      <button
                        onClick={handleStopPlayback}
                        className="text-[11px] text-[#F4EFE7]/35 hover:text-[#F4EFE7]/65 transition-colors"
                        data-testid="playback-stop-btn"
                      >
                        {lang === 'es' ? 'Detener' : 'Stop'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Part markers, so the walkthrough has a visible shape */}
                  <div className="flex items-center gap-1.5 mt-2.5">
                    {playbackSections.map((s, i) => (
                      <div 
                        key={s.key}
                        className="h-[3px] flex-1 rounded-full overflow-hidden"
                        style={{ background: 'rgba(255,255,255,0.07)' }}
                      >
                        <div 
                          className="h-full rounded-full"
                          style={{
                            width: i < activeSectionIndex 
                              ? '100%' 
                              : i === activeSectionIndex && playbackDuration > 0
                                ? `${Math.min(100, (playbackProgress / playbackDuration) * 100)}%`
                                : '0%',
                            background: 'linear-gradient(90deg, #E8C58F 0%, #C9A054 100%)',
                            transition: 'width 0.25s linear'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-1.5 text-[11px] text-[#F4EFE7]/25 tabular-nums">
                    <span>{formatTime(playbackProgress)}</span>
                    <span>{playbackDuration > 0 ? formatTime(playbackDuration) : ''}</span>
                  </div>
                </div>
              ) : playbackState === 'error' ? (
                <p className="mt-2.5 text-center text-xs text-[#F4EFE7]/45" data-testid="playback-error">
                  {lang === 'es'
                    ? 'No pude reproducir el audio ahora mismo. Tu reflexión sigue aquí.'
                    : "I couldn't play the audio just now. Your reflection is still here."}
                </p>
              ) : (
                <p className="mt-2 text-center text-[11px] text-[#F4EFE7]/28">
                  {lang === 'es'
                    ? `${playbackSections.length} partes · ${totalListenTime} · los símbolos y la pregunta no se narran`
                    : `${playbackSections.length} parts · ${totalListenTime} · symbols and the question aren't narrated`}
                </p>
              )}
            </motion.div>
          )}
          
          {/* Emotional Summary — first narrated part */}
          {dream.emotional_summary && (
            <NarratedSection
              icon={Moon}
              title={lang === 'es' ? 'Resumen Emocional' : 'Emotional Summary'}
              delay={0.3}
              isActive={activeSectionKey === 'emotional_summary'}
              bodyClass="text-[#F4EFE7]/85"
              testId="section-header-emotional-summary"
              listen={(
                <SectionListenPill
                  state={sectionPlaybackState('emotional_summary')}
                  onClick={() => handleSectionListen('emotional_summary')}
                  estimate={estimateClipTime(dream.emotional_summary)}
                  lang={lang}
                  testId="section-listen-emotional_summary"
                />
              )}
            >
              {dream.emotional_summary}
            </NarratedSection>
          )}
          
          {/* Arvexis's Reflection — second narrated part */}
          {dream.integrated_interpretation && (
            <NarratedSection
              icon={Sparkles}
              title={lang === 'es' ? 'La Reflexión de Arvexis' : "Arvexis's Reflection"}
              variant="gold"
              delay={0.35}
              isActive={activeSectionKey === 'interpretation'}
              bodyClass="text-[#F4EFE7]/92"
              testId="section-header-reflection"
              extra={(
                <button
                  onClick={() => handleCopy(dream.integrated_interpretation, 'reflection')}
                  className="p-1.5 rounded-full hover:bg-white/5 transition-colors"
                  data-testid="copy-reflection-btn"
                >
                  {copyFeedback === 'reflection' ? (
                    <Check size={14} className="text-[#E8C58F]" />
                  ) : (
                    <Copy size={14} className="text-[#F4EFE7]/35" />
                  )}
                </button>
              )}
              listen={(
                <SectionListenPill
                  state={sectionPlaybackState('interpretation')}
                  onClick={() => handleSectionListen('interpretation')}
                  estimate={estimateClipTime(dream.integrated_interpretation)}
                  lang={lang}
                  testId="section-listen-interpretation"
                />
              )}
            >
              {dream.integrated_interpretation}
            </NarratedSection>
          )}
          
          {/* Grounding Insight — final narrated part */}
          {dream.grounding_insight && (
            <NarratedSection
              icon={Leaf}
              title={lang === 'es' ? 'Perspectiva de Conexión' : 'Grounding Insight'}
              delay={0.4}
              isActive={activeSectionKey === 'grounding'}
              bodyClass="text-[#F4EFE7]/85"
              testId="section-header-grounding"
              listen={(
                <SectionListenPill
                  state={sectionPlaybackState('grounding')}
                  onClick={() => handleSectionListen('grounding')}
                  estimate={estimateClipTime(dream.grounding_insight)}
                  lang={lang}
                  testId="section-listen-grounding"
                />
              )}
            >
              {dream.grounding_insight}
            </NarratedSection>
          )}
          
          {/* A Question to Reflect On — visible, never narrated */}
          {(dream.reflection_question || dream.reflection_questions?.[0]) && (
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mb-7 p-5 md:p-6 rounded-2xl"
              style={{
                background: 'linear-gradient(145deg, rgba(232,197,143,0.055) 0%, rgba(232,197,143,0.012) 100%)',
                border: '1px solid rgba(232,197,143,0.13)'
              }}
              data-testid="reflection-question-card"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <span 
                  className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(232,197,143,0.12)', border: '1px solid rgba(232,197,143,0.2)' }}
                >
                  <HelpCircle size={13} className="text-[#E8C58F]" />
                </span>
                <h2 
                  className="text-[#F4EFE7]/90 text-base md:text-lg font-light"
                  style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
                >
                  {lang === 'es' ? 'Una Pregunta Para Reflexionar' : 'A Question to Reflect On'}
                </h2>
              </div>
              <p 
                className="text-[#F4EFE7]/90 text-[18px] leading-[1.65] italic"
                style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
              >
                {dream.reflection_question || dream.reflection_questions[0]}
              </p>
            </motion.div>
          )}
          
          {/* ── CONTINUE TALKING · SAVE · SHARE ── */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="flex flex-col sm:flex-row items-stretch gap-3 mb-6"
          >
            <button
              onClick={() => setShowConversation(true)}
              disabled={showConversation}
              className="flex-1 flex items-center justify-center gap-2.5 px-5 py-4 rounded-xl transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-40"
              style={{ 
                background: 'rgba(232, 197, 143, 0.06)',
                border: '1px solid rgba(232, 197, 143, 0.56)',
                color: '#F4EFE7'
              }}
              data-testid="explore-dream-btn"
            >
              <MessageCircle size={17} />
              <span className="text-sm font-medium text-left leading-tight">
                {lang === 'es' ? 'Seguir Hablando' : 'Continue Talking'}
                <span className="block text-[11px] opacity-70">
                  {lang === 'es' ? 'Sobre Este Sueño' : 'About This Dream'}
                </span>
              </span>
            </button>
            <div className="flex items-stretch gap-3">
              <button
                onClick={handleOpenDreamCard}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-sm text-[#F4EFE7]/70 hover:text-[#F4EFE7] transition-all"
                style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(244,239,231,0.09)' }}
                data-testid="save-keepsake-inline-btn"
              >
                <Download size={16} />
                <span>{lang === 'es' ? 'Guardar' : 'Save'}</span>
              </button>
              <button
                onClick={handleOpenDreamCard}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-sm text-[#F4EFE7]/70 hover:text-[#F4EFE7] transition-all"
                style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(244,239,231,0.09)' }}
                data-testid="share-inline-btn"
              >
                <Share2 size={16} />
                <span>{lang === 'es' ? 'Compartir' : 'Share'}</span>
              </button>
            </div>
          </motion.div>
          
          {/* Conversation panel opens in place */}
          {showConversation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="mb-8"
            >
              <div 
                className="rounded-2xl overflow-hidden"
                style={{
                  background: 'linear-gradient(180deg, rgba(11,12,19,0.96) 0%, rgba(7,8,13,0.96) 100%)',
                  border: '1px solid rgba(232,197,143,0.12)',
                  minHeight: '420px'
                }}
              >
                <DreamConversation
                  dreamId={dream._id || dream.id}
                  lang={lang}
                  onClose={() => setShowConversation(false)}
                  onConversationEnd={(data) => {
                    console.log('Conversation ended:', data);
                  }}
                />
              </div>
            </motion.div>
          )}
          
          {/* Privacy reassurance */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex items-center justify-center gap-2 py-2"
            data-testid="privacy-reassurance"
          >
            <Lock size={11} className="text-[#F4EFE7]/25" />
            <span className="text-[#F4EFE7]/30 text-xs">
              {lang === 'es' ? 'Privado por defecto.' : 'Private by default.'}
            </span>
          </motion.div>
          
          {/* Footer Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="py-10 text-center"
          >
            <p className="text-[#F4EFE7]/20 text-xs">
              {lang === 'es' 
                ? 'Una perspectiva para explorar — tú eres el experto en tus propios sueños.'
                : 'One perspective to explore — you are the expert on your own dreams.'}
            </p>
          </motion.div>
          
        </main>
      </motion.div>
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MODALS */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      
      {/* Copy Feedback Toast */}
      <AnimatePresence>
        {copyFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-28 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-xl"
            style={{
              background: 'rgba(232, 197, 143, 0.12)',
              border: '1px solid rgba(232, 197, 143, 0.25)'
            }}
            data-testid="copy-toast"
          >
            <Check size={16} className="text-[#E8C58F]" />
            <span className="text-[#E8C58F] text-sm font-medium">
              {lang === 'es' ? 'Copiado' : 'Copied'}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Dream Card — the one member-facing save/share artifact */}
      <AnimatePresence>
        {showDreamCard && dream && (
          <DreamCard
            dream={dream}
            lang={lang}
            onClose={() => setShowDreamCard(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PremiumReflectionScreen;
